import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { components } from "@/api/schema";
import { RequestError } from "@/app/queries";

/** Ревью (SP4): очередь ментора, сдача с тредами, вердикт, карточка студента,
 *  заметки, лента уведомлений. Корзины/сортировки очереди — на клиенте из строк. */
export type QueueItem = components["schemas"]["QueueItem"];
export type SubmissionView = components["schemas"]["SubmissionView"];
export type SubmissionBrief = components["schemas"]["SubmissionBrief"];
export type Comment = components["schemas"]["Comment"];
export type Thread = components["schemas"]["Thread"];
export type Todo = components["schemas"]["Todo"];
export type CommentInput = components["schemas"]["CommentInput"];
export type StudentCard = components["schemas"]["StudentCard"];
export type Note = components["schemas"]["Note"];
export type ReasonTag = components["schemas"]["ReasonTag"];
export type Flag = components["schemas"]["Flag"];
export type Notification = components["schemas"]["Notification"];
export type QueueMode = "mine" | "all" | "nocohort";
export type QueueStatus = "on_review" | "changes_requested" | "accepted" | "any";

function fail(status: number, error: unknown): never {
  const e = error as { error?: string; message?: string } | undefined;
  throw new RequestError(status, e?.error ?? "unknown", e?.message ?? "Не удалось выполнить запрос.");
}

export const reviewKeys = {
  queue: (mode: QueueMode, status: QueueStatus) => ["review", "queue", mode, status] as const,
  submission: (id: string) => ["review", "submission", id] as const,
  mySubmissions: (assignmentId: string) => ["review", "my", assignmentId] as const,
  student: (userId: string) => ["review", "student", userId] as const,
  reasons: ["review", "reasons"] as const,
  notifications: ["notifications"] as const,
};

const noRetry4xx = (n: number, err: unknown) => !(err instanceof RequestError && err.status >= 400 && err.status < 500) && n < 2;

export function useQueue(mode: QueueMode, status: QueueStatus = "on_review") {
  return useQuery({
    queryKey: reviewKeys.queue(mode, status),
    queryFn: async () => {
      const { data, error, response } = await api.GET("/mentor/queue", { params: { query: { mode, status } } });
      if (!data) fail(response.status, error);
      return data;
    },
    retry: noRetry4xx,
  });
}

export function useSubmission(id: string | undefined) {
  return useQuery({
    queryKey: reviewKeys.submission(id ?? ""),
    enabled: !!id,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/submissions/{submissionId}", { params: { path: { submissionId: id! } } });
      if (!data) fail(response.status, error);
      return data;
    },
    retry: noRetry4xx,
  });
}

/** Сдачи студента по заданию (S7), новые сверху. */
export function useMySubmissions(assignmentId: string) {
  return useQuery({
    queryKey: reviewKeys.mySubmissions(assignmentId),
    queryFn: async () => {
      const { data, error, response } = await api.GET("/assignments/{assignmentId}/submissions", { params: { path: { assignmentId } } });
      if (!data) fail(response.status, error);
      return data;
    },
    retry: noRetry4xx,
  });
}

export function useStudentCard(userId: string) {
  return useQuery({
    queryKey: reviewKeys.student(userId),
    queryFn: async () => {
      const { data, error, response } = await api.GET("/mentor/students/{userId}", { params: { path: { userId } } });
      if (!data) fail(response.status, error);
      return data;
    },
    retry: noRetry4xx,
  });
}

export function useReasons() {
  return useQuery({
    queryKey: reviewKeys.reasons,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/review/reasons");
      if (!data) fail(response.status, error);
      return data;
    },
    staleTime: 10 * 60_000,
  });
}

function invalidateSubmission(qc: ReturnType<typeof useQueryClient>, id: string) {
  void qc.invalidateQueries({ queryKey: reviewKeys.submission(id) });
  void qc.invalidateQueries({ queryKey: ["review", "queue"] });
  void qc.invalidateQueries({ queryKey: ["review", "student"] });
  void qc.invalidateQueries({ queryKey: ["review", "my"] });
}

/** «+ комментарий» (ментор) или ответ в треде (студент — только parent_id). */
export function useAddComment(submissionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CommentInput) => {
      const { data, error, response } = await api.POST("/submissions/{submissionId}/comments", { params: { path: { submissionId } }, body });
      if (!data) fail(response.status, error);
      return data;
    },
    onSuccess: () => invalidateSubmission(qc, submissionId),
  });
}

/** Вердикт: 409 no_comments несёт дословную причину в message. */
export function useVerdict(submissionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { status: "accepted" | "changes_requested"; summary?: string }) => {
      const { data, error, response } = await api.POST("/submissions/{submissionId}/verdict", { params: { path: { submissionId } }, body });
      if (!data) fail(response.status, error);
      return data;
    },
    onSuccess: (view) => {
      qc.setQueryData(reviewKeys.submission(submissionId), view);
      invalidateSubmission(qc, submissionId);
    },
  });
}

export function useSaveChecklist(submissionId: string) {
  return useMutation({
    mutationFn: async (items: Record<string, boolean>) => {
      const { error, response } = await api.PUT("/submissions/{submissionId}/checklist", { params: { path: { submissionId } }, body: { items } });
      if (!response.ok) fail(response.status, error);
    },
  });
}

export function useAddNote(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: string) => {
      const { data, error, response } = await api.POST("/mentor/students/{userId}/notes", { params: { path: { userId } }, body: { body } });
      if (!data) fail(response.status, error);
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: reviewKeys.student(userId) }),
  });
}

/** Лента: опрос раз в 30 с (SSE-клиента в SPA пока нет). */
export function useNotifications(enabled = true) {
  return useQuery({
    queryKey: reviewKeys.notifications,
    enabled,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/notifications");
      if (!data) fail(response.status, error);
      return data;
    },
    retry: noRetry4xx,
    refetchInterval: 30_000,
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { all?: boolean; ids?: string[] }) => {
      const { error, response } = await api.POST("/notifications/read", { body });
      if (!response.ok) fail(response.status, error);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: reviewKeys.notifications }),
  });
}

/** Время ожидания одной функцией для M1/M2/M3: «31 ч», «2 д 7 ч», «меньше часа». */
export function waitLabel(hours: number): string {
  if (hours < 1) return "меньше часа";
  const h = Math.floor(hours);
  if (h < 48) return `${h} ч`;
  const d = Math.floor(h / 24);
  const rest = h % 24;
  return rest ? `${d} д ${rest} ч` : `${d} д`;
}
